'use client';

import ProductCard from '@/components/Product/ProductCard';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import type { ProductRecommendConfig } from '../../../../types';
import { useCampaignRecommendTracking } from '../../../SeedingPage/hooks/useCampaignRecommendTracking';
import { useCapsuleAppCartRecommendReady } from './useCapsuleAppCartRecommendReady';
import {
  useCapsuleAlgorithmRecommendProducts,
  useCapsuleGroupRecommendProducts,
  type CapsuleRecommendProductsState,
} from './useCapsuleRecommendProducts';
import type { CapsuleProductRecommendProps, CapsuleRecommendProduct } from './types';

const CAPSULE_RECOMMEND_SECTION_CLASS = {
  root: (isWap: boolean) =>
    `bg-white font-regular w-full ${isWap ? 'mx-auto px-[8px] pt-[24px]' : 'mx-auto px-[48px] pt-[40px]'} pb-[60px]`,
  title: (isWap: boolean) =>
    `font-bb text-center ${isWap ? 'mb-[16px] text-[20px] leading-[24px]' : 'mb-[32px] text-[32px] leading-[40px]'}`,
  grid: (isWap: boolean) =>
    isWap
      ? 'relative grid grid-cols-2 gap-x-[8px] gap-y-[40px]'
      : 'grid gap-x-[16px] gap-y-[24px] sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',
} as const;

export function isCapsuleProductRecommendEnabled(config?: ProductRecommendConfig): boolean {
  if (!config?.source_type) return false;
  if (config.source_type === 'algorithm') return true;
  return config.source_type === 'group' && Number(config.group_id) > 0;
}

interface CapsuleRecommendSectionProps
  extends Pick<
      CapsuleProductRecommendProps,
      | 'deviceType'
      | 'onProductCollect'
      | 'onProductClick'
      | 'onProductExposure'
      | 'onViewMoreClick'
      | 'runtimeConfig'
    >,
    CapsuleRecommendProductsState {
  title?: string;
}

function CapsuleRecommendSection({
  deviceType,
  hasMore,
  loading,
  loadMore,
  onProductCollect,
  onProductClick,
  onProductExposure,
  onViewMoreClick,
  products,
  runtimeConfig,
  title,
}: CapsuleRecommendSectionProps) {
  const isWap = deviceType !== EPlatform.WEB;
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const tCart = useTranslations('cart');
  const sectionTitle = title || tCart('youMayAlsoLike.title');
  const modelExposureTrackedRef = useRef(false);
  const viewMoreButtonRef = useRef<HTMLButtonElement>(null);
  const loadCompleteStatusRef = useRef<HTMLSpanElement>(null);
  const shouldRestoreLoadMoreFocusRef = useRef(false);
  const { ref: titleRef, inView: titleInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const {
    collectScene,
    trackRecommendClick,
    trackRecommendCollectClick,
    trackRecommendModelExp,
    trackRecommendValidExp,
  } = useCampaignRecommendTracking(runtimeConfig);

  useEffect(() => {
    if (!titleInView || products.length === 0 || modelExposureTrackedRef.current) return;
    modelExposureTrackedRef.current = true;
    trackRecommendModelExp();
  }, [products.length, titleInView, trackRecommendModelExp]);

  useEffect(() => {
    if (loading || !shouldRestoreLoadMoreFocusRef.current) return;

    if (!hasMore) loadCompleteStatusRef.current?.focus();
    shouldRestoreLoadMoreFocusRef.current = false;
  }, [hasMore, loading]);

  const handleProductClick = useCallback(
    (item: CapsuleRecommendProduct, index: number) => {
      if (onProductClick) {
        trackRecommendClick(item, index, (clickedItem, clickedIndex) => {
          onProductClick(clickedItem, clickedIndex, sectionTitle);
        });
      } else {
        trackRecommendClick(item, index);
      }
    },
    [onProductClick, sectionTitle, trackRecommendClick],
  );

  const handleProductExposure = useCallback(
    (item: CapsuleRecommendProduct, index: number) => {
      if (onProductExposure) {
        onProductExposure(item, index, sectionTitle);
      } else {
        trackRecommendValidExp(item, index);
      }
    },
    [onProductExposure, sectionTitle, trackRecommendValidExp],
  );

  const handleProductCollect = useCallback(
    (item: CapsuleRecommendProduct, index: number, collectType: string) => {
      if (onProductCollect) {
        onProductCollect(item, index, collectType);
      } else {
        trackRecommendCollectClick(item, index, collectType);
      }
    },
    [onProductCollect, trackRecommendCollectClick],
  );

  if (products.length === 0 && !loading) return null;

  return (
    <div className={CAPSULE_RECOMMEND_SECTION_CLASS.root(isWap)}>
      <div ref={titleRef} className={CAPSULE_RECOMMEND_SECTION_CLASS.title(isWap)}>
        {sectionTitle}
      </div>

      <div className={CAPSULE_RECOMMEND_SECTION_CLASS.grid(isWap)}>
        {products.map((item, index) => {
          const productForCard = {
            ...item,
            name: item.name || item.short_name || item.shop_sku || '',
          };

          return (
            <div key={`${item.shop_sku || item.id}-${index}`} className="bg-none">
              <ProductCard
                product={productForCard}
                index={index}
                showTryOn={false}
                handelClick={() => handleProductClick(item, index)}
                onValidExposure={() => handleProductExposure(item, index)}
                wished={() => handleProductCollect(item, index, '收藏')}
                removed={() => handleProductCollect(item, index, '取消收藏')}
                collectScene={collectScene}
                className={isWap ? 'bg-none' : 'h-full bg-none'}
                imageClassName={isWap ? 'h-[176px] rounded-[12px]' : undefined}
                showCorrelation
              />
            </div>
          );
        })}
      </div>

      {hasMore ? (
        <div className={`flex justify-center ${isWap ? 'mt-[24px]' : 'mt-[56px]'}`}>
          <button
            ref={viewMoreButtonRef}
            type="button"
            className={`flex items-center justify-center rounded-[999px] border border-solid border-auxiliary-dark-gray bg-transparent font-bb text-primary-black transition-opacity ${
              isWap ? 'h-[43px] w-[282px] max-w-full text-[14px]' : 'h-[46px] w-[330px] text-16'
            } ${loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-80'}`}
            onClick={() => {
              shouldRestoreLoadMoreFocusRef.current =
                document.activeElement === viewMoreButtonRef.current;
              onViewMoreClick?.();
              void loadMore();
            }}
            disabled={loading}
            aria-busy={loading}
          >
            {tMarketing('common.viewMore')}
          </button>
        </div>
      ) : null}
      <span
        ref={loadCompleteStatusRef}
        className="sr-only"
        role="status"
        aria-atomic="true"
        aria-live="polite"
        tabIndex={-1}
      >
        {!hasMore && products.length > 0 ? tCart('noMore') : ''}
      </span>
    </div>
  );
}

interface CapsuleGroupRecommendProps extends Omit<CapsuleProductRecommendProps, 'config'> {
  groupId: number;
  title?: string;
}

function CapsuleGroupRecommend({
  groupId,
  onInitialLoadComplete,
  title,
  ...sectionProps
}: CapsuleGroupRecommendProps) {
  const recommendState = useCapsuleGroupRecommendProducts(groupId, onInitialLoadComplete);
  return <CapsuleRecommendSection {...sectionProps} {...recommendState} title={title} />;
}

interface CapsuleAlgorithmRecommendProps extends Omit<CapsuleProductRecommendProps, 'config'> {
  algorithmValue?: string;
  title?: string;
}

function CapsuleAlgorithmRecommend({
  algorithmValue,
  onInitialLoadComplete,
  title,
  ...sectionProps
}: CapsuleAlgorithmRecommendProps) {
  const recommendState = useCapsuleAlgorithmRecommendProducts(
    algorithmValue,
    onInitialLoadComplete,
  );
  return <CapsuleRecommendSection {...sectionProps} {...recommendState} title={title} />;
}

function CapsuleCartAlgorithmRecommend({
  deviceType,
  onInitialLoadComplete,
  title,
  ...sectionProps
}: Omit<CapsuleAlgorithmRecommendProps, 'algorithmValue'>) {
  const cartContextReady = useCapsuleAppCartRecommendReady(deviceType);
  const recommendState = useCapsuleAlgorithmRecommendProducts(
    'cart',
    onInitialLoadComplete,
    cartContextReady,
  );

  return (
    <CapsuleRecommendSection
      {...sectionProps}
      {...recommendState}
      deviceType={deviceType}
      title={title}
    />
  );
}

export default function CapsuleProductRecommend({
  config,
  ...recommendProps
}: CapsuleProductRecommendProps) {
  const token = useStore((state) => state.token);

  if (!isCapsuleProductRecommendEnabled(config)) return null;

  if (config.source_type === 'group' && config.group_id) {
    return (
      <CapsuleGroupRecommend
        key={`capsule-group-recommend-${config.group_id}`}
        {...recommendProps}
        groupId={config.group_id}
        title={config.section_title}
      />
    );
  }

  if (config.source_type === 'algorithm') {
    if (config.algorithm_value === 'cart') {
      return (
        <CapsuleCartAlgorithmRecommend
          key={`capsule-cart-algorithm-recommend-${recommendProps.deviceType}`}
          {...recommendProps}
          title={config.section_title}
        />
      );
    }

    return (
      <CapsuleAlgorithmRecommend
        key={`capsule-algorithm-recommend-${recommendProps.deviceType}-${token}`}
        {...recommendProps}
        algorithmValue={config.algorithm_value}
        title={config.section_title}
      />
    );
  }

  return null;
}

export type {
  CapsuleRecommendProductCollectHandler,
  CapsuleRecommendProductTrackingHandler,
} from './types';
